import { LoadingOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Background from '~/components/Background';
import Button from '~/components/Button';
import Cardbox from '~/components/Cardbox';
import CardboxWrapper from '~/components/CardboxWrapper';
import Gap from '~/components/Gap';
import Text from '~/components/Text';
import { useIsDesktop } from '~/hooks/useIsDesktop';
import { verifyPaymentApi } from '~/api';
import { apiResponseHandler, ApiResponseEnum } from '~/utils/api';
import { setPaymentCompleted, setCurrentPlan } from '~/store/slices/user';
import { PlanType } from '~/utils/payment';

const StatusIcon = styled.div<{ status: 'loading' | 'success' | 'error' }>`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
  font-size: 48px;

  color: ${props => {
    switch (props.status) {
      case 'success':
        return '#52c41a';
      case 'error':
        return '#ff4d4f';
      default:
        return '#1890ff';
    }
  }};
`;

const PlanBadge = styled.div<{ plan: PlanType }>`
  display: inline-block;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props =>
    props.plan === 'pro'
      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'};
  color: white;
  font-size: 14px;
  margin: 16px 0;
`;

interface VerificationResponse {
  plan: PlanType;
}

const VerifySubscription = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useIsDesktop();

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [plan, setPlan] = useState<PlanType | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // Extract URL parameters
        const urlParams = new URLSearchParams(location.search);
        const paymentId = urlParams.get('payment_id');
        const subscriptionId = urlParams.get('subscription_id');
        const status = urlParams.get('status');

        // Check if we have either payment_id or subscription_id
        if ((!paymentId && !subscriptionId) || !status) {
          throw new Error('Missing payment parameters');
        }

        // Prepare API parameters based on payment type
        const apiParams = paymentId
          ? { payment_id: paymentId, payment_status: status }
          : { subscription_id: subscriptionId!, status };

        // Call verification API
        const response = await apiResponseHandler<VerificationResponse>(
          verifyPaymentApi(apiParams),
        );

        if (response.type === ApiResponseEnum.Success) {
          setStatus('success');
          setPlan(response.data.plan);

          // Update Redux state
          dispatch(setPaymentCompleted(true));
          dispatch(setCurrentPlan(response.data.plan));

          toast.success('Subscription updated successfully!');
        } else {
          throw new Error(response.error || 'Payment verification failed');
        }
      } catch (error) {
        console.error('Payment verification error:', error);
        setStatus('error');
        setErrorMessage(error instanceof Error ? error.message : 'Payment verification failed');
        toast.error('Payment verification failed');
      }
    };

    verifyPayment();
  }, [dispatch, location.search]);

  const handleGoToDashboard = () => {
    navigate('/');
  };

  const handleRetry = () => {
    setStatus('loading');
    setErrorMessage('');
    // Re-run verification
    window.location.reload();
  };

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <>
            <StatusIcon status="loading">
              <LoadingOutlined />
            </StatusIcon>
            <Text align="center" size="lg">
              Verifying Your Payment
            </Text>
            <Gap t={16} />
            <Text align="center" variant="neutral">
              Please wait while we confirm your subscription...
            </Text>
          </>
        );

      case 'success':
        return (
          <>
            <StatusIcon status="success">
              <CheckCircleOutlined />
            </StatusIcon>
            <Text align="center" size={isDesktop ? 'slg' : 'md'} className="bold">
              Payment Verified Successfully!
            </Text>
            <Gap t={16} />
            {plan && (
              <div className="center">
                <PlanBadge plan={plan}>{plan === 'pro' ? 'Pro Plan' : 'Infinity Plan'}</PlanBadge>
              </div>
            )}
            <Gap t={16} />
            <Text align="center" variant="neutral">
              Your subscription has been activated. You now have access to all premium features.
            </Text>
            <Gap t={32} />
            <Button size="full" onClick={handleGoToDashboard}>
              Go to Dashboard
            </Button>
          </>
        );

      case 'error':
        return (
          <>
            <StatusIcon status="error">
              <CloseCircleOutlined />
            </StatusIcon>
            <Text align="center" size={isDesktop ? 'slg' : 'md'} className="bold">
              Payment Verification Failed
            </Text>
            <Gap t={16} />
            <Text align="center" variant="neutral">
              {errorMessage ||
                'We encountered an issue while verifying your payment. Please try again.'}
            </Text>
            <Gap t={32} />
            <Button size="full" onClick={handleRetry}>
              Try Again
            </Button>
            <Gap t={16} />
            <Button variant="secondary" size="full" onClick={handleGoToDashboard}>
              Go to Dashboard
            </Button>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Background key="verify-subscription" data-component-name="VerifySubscription">
      <CardboxWrapper className="center">
        <Cardbox width="480px">
          {renderContent()}

          <Gap t={24} />

          <Text align="center" size="xs" variant="neutral">
            If you continue to experience issues, please contact our support team.
          </Text>
        </Cardbox>
      </CardboxWrapper>
    </Background>
  );
};

export default VerifySubscription;
